// Languages of the worker-facing SOS Field app (/field): English and Spanish.
// Text stays in English in the code; `ft('English text')` returns the Spanish from locales/field-es when the
// worker chose Spanish, and the English otherwise (also for anything not translated yet).
import { ref } from 'vue'
import spanish from '../locales/field-es'

export type FieldLocale = 'en' | 'es'

const STORAGE_KEY = 'field-locale'

const initialLocale = (): FieldLocale => {
  if (!import.meta.client) return 'en'
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'en' || saved === 'es') return saved
  } catch {
    // Private mode: fall back to the phone's language.
  }
  return navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en'
}

export const fieldLocale = ref<FieldLocale>(initialLocale())

export function setFieldLocale(locale: FieldLocale) {
  fieldLocale.value = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Not saved; the choice lasts until the page closes.
  }
  if (import.meta.client) document.documentElement.lang = locale
}

/** Translate English UI text for the field app. `{name}` placeholders are filled from `params`. */
export function ft(text: string, params?: Record<string, string | number>) {
  let result = fieldLocale.value === 'es' ? (spanish[text] ?? text) : text
  if (params)
    for (const [key, value] of Object.entries(params))
      result = result.replaceAll(`{${key}}`, String(value))
  return result
}

/** Locale for dates and numbers on the phone (`toLocaleString`). */
export const fieldDateLocale = () =>
  fieldLocale.value === 'es' ? 'es-US' : 'en-US'
